import { PropsWithChildren } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useLocation } from "react-router-dom";

import ErrorFallBack from "./ErrorFallBack";
import { reloadPageOnce } from "../reloadPage";

// A tab on the previous build gets a 404 on a lazy route after a deploy: a
// reload fixes it. Within the cooldown it did not help, so show the fallback.
const CHUNK_ERROR_MESSAGE =
  /Loading (CSS )?chunk .+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i;

const isChunkLoadError = (error: Error): boolean =>
  error.name === "ChunkLoadError" || CHUNK_ERROR_MESSAGE.test(error.message);

const reloadOnChunkError = (error: Error): void => {
  if (!isChunkLoadError(error)) {
    return;
  }
  reloadPageOnce("Lazy chunk failed to load");
};

const ChunkErrorBoundary = ({ children }: PropsWithChildren) => {
  const location = useLocation();

  return (
    <ErrorBoundary
      fallback={<ErrorFallBack />}
      onError={reloadOnChunkError}
      // Without this the fallback would stick for the rest of the session:
      // leaving the route that failed to load gives the app a chance to render.
      resetKeys={[location.pathname]}
    >
      {children}
    </ErrorBoundary>
  );
};

export default ChunkErrorBoundary;
